import React, { Suspense } from "react";
import { Spinner } from "@/components/ui";
import { EmptyView } from "@/components/EmptyView";

const ResponsiveBar = React.lazy(() => import("@nivo/bar").then(m => ({ default: m.ResponsiveBar })));

interface ChartProps {
  data: any[];
  title?: string;
  indexBy: string;
}

export const HorizontalBarChart: React.FC<ChartProps> = ({ data, indexBy }) => {
  if (!data || data.length === 0) {
    return (
      <div className="py-8">
        <EmptyView text="No clicks recorded for this metric" />
      </div>
    );
  }

  const chartData = data.slice(0, 10).map(d => ({
    label: d[indexBy] || "Unknown",
    clicks: d.count,
  }));

  // Calculate dynamic height: 35px per bar + 100px for axes and margins
  // Capped at 400px, minimum 200px
  const dynamicHeight = Math.min(400, Math.max(200, chartData.length * 35 + 100));

  return (
    <div style={{ height: `${dynamicHeight}px` }} className="w-full">
      <Suspense
        fallback={
          <div className="h-full flex items-center justify-center">
            <Spinner />
          </div>
        }
      >
        <ResponsiveBar
          data={chartData}
          keys={["clicks"]}
          indexBy="label"
          layout="horizontal"
          margin={{ top: 10, right: 30, bottom: 50, left: 120 }}
          padding={0.3}
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
          colors={{ scheme: "paired" }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: "Clicks",
            legendPosition: "middle",
            legendOffset: 32,
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          role="application"
        />
      </Suspense>
    </div>
  );
};
